import { encryptData, decryptData } from './encryption';
import { loadPayoutsFromStorage, loadSettingsFromStorage, savePayoutsToStorage, saveSettingsToStorage } from './storage';
import { loadMiningConfigs } from './miningStorage';
import { Sentry } from './sentry';
import { payoutApiService } from '../services/payoutApiService';
import { getAuthState } from '../contexts/AuthContext';

// Clés de stockage utilisées dans l'application
const STORAGE_KEYS = [
  'cryptotrack_payouts',
  'cryptotrack_settings',
  'cryptotrack_mining_configs',
  'cryptotrack_encrypted_payouts',
  'cryptotrack_encrypted_settings'
];

import type { Payout } from '../types/payout';
import type { GlobalSettings } from '../types/settings';
import type { MiningConfigs } from '../types/mining';

// Interface pour les données exportées
export interface ExportedData {
  payouts: Payout[];
  settings: GlobalSettings;
  miningConfigs: MiningConfigs;
  exportDate: string;
  version: string;
}

// Effacer uniquement les données du localStorage
export const clearLocalStorageOnly = async (): Promise<void> => {
  try {
    STORAGE_KEYS.forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('Données du localStorage supprimées');
  } catch (error) {
    console.error('Erreur lors de la suppression des données locales:', error);
    Sentry.captureException(error, {
      tags: { operation: 'clearLocalStorageOnly' }
    });
    throw new Error('Impossible de supprimer les données locales');
  }
};

// Effacer uniquement les données du serveur (pour utilisateurs connectés)
export const clearServerDataOnly = async (): Promise<void> => {
  try {
    const authState = getAuthState();

    if (!authState.isAuthenticated) {
      throw new Error('Vous devez être connecté pour supprimer les données du serveur');
    }

    await payoutApiService.deleteAll();
    console.log('Tous les payouts supprimés du serveur');
  } catch (error) {
    console.error('Erreur lors de la suppression des données du serveur:', error);
    Sentry.captureException(error, {
      tags: { operation: 'clearServerDataOnly' }
    });
    throw new Error('Impossible de supprimer les données du serveur');
  }
};

// Effacer toutes les données (localStorage + serveur si connecté)
export const clearAllData = async (): Promise<void> => {
  try {
    // Vérifier si l'utilisateur est authentifié
    const authState = getAuthState();

    // Si utilisateur connecté : supprimer aussi du serveur
    if (authState.isAuthenticated) {
      try {
        await payoutApiService.deleteAll();
        console.log('Tous les payouts supprimés du serveur');
      } catch (apiError) {
        console.error('Erreur lors de la suppression des données du serveur:', apiError);
        Sentry.captureException(apiError, {
          tags: { operation: 'clearAllData_apiDelete' }
        });
        throw new Error('Impossible de supprimer les données du serveur');
      }
    }

    // Supprimer du localStorage
    STORAGE_KEYS.forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('Toutes les données ont été supprimées');
  } catch (error) {
    console.error('Erreur lors de la suppression des données:', error);
    Sentry.captureException(error, {
      tags: { operation: 'clearAllData' }
    });
    throw new Error('Impossible de supprimer les données');
  }
};

// Exporter toutes les données chiffrées
export const exportEncryptedData = async (password: string): Promise<string> => {
  try {
    // Vérifier si l'utilisateur est authentifié
    const authState = getAuthState();

    let data: ExportedData;

    if (authState.isAuthenticated) {
      // Utilisateur connecté : récupérer les données depuis le serveur
      try {
        const exportData = await payoutApiService.exportAll();
        data = {
          payouts: exportData.payouts,
          settings: exportData.settings as GlobalSettings,
          miningConfigs: exportData.miningConfigs as MiningConfigs,
          exportDate: exportData.exportDate,
          version: exportData.version
        };
        console.log('Données exportées depuis le serveur');
      } catch (apiError) {
        console.error('Erreur lors de l\'export depuis le serveur, fallback sur localStorage:', apiError);
        Sentry.captureException(apiError, {
          tags: { operation: 'exportEncryptedData_apiExport' }
        });
        // Fallback sur localStorage en cas d'erreur API
        data = {
          payouts: await loadPayoutsFromStorage(),
          settings: await loadSettingsFromStorage(),
          miningConfigs: await loadMiningConfigs(),
          exportDate: new Date().toISOString(),
          version: '1.0.0'
        };
      }
    } else {
      // Utilisateur invité : collecter les données depuis localStorage
      data = {
        payouts: await loadPayoutsFromStorage(),
        settings: await loadSettingsFromStorage(),
        miningConfigs: await loadMiningConfigs(),
        exportDate: new Date().toISOString(),
        version: '1.0.0'
      };
    }

    // Convertir en JSON
    const jsonData = JSON.stringify(data, null, 2);

    // Chiffrer les données
    const encryptedData = await encryptData(jsonData, password);

    return encryptedData;
  } catch (error) {
    console.error('Erreur lors de l\'export des données:', error);
    Sentry.captureException(error, {
      tags: { operation: 'exportEncryptedData' }
    });
    throw new Error('Impossible d\'exporter les données');
  }
};

// Importer des données chiffrées
export const importEncryptedData = async (
  encryptedData: string, 
  password: string,
  options: {
    replaceExisting?: boolean;
    mergeData?: boolean;
  } = { replaceExisting: true }
): Promise<void> => {
  try {
    // Déchiffrer les données
    const decryptedJson = await decryptData(encryptedData, password);
    const importedData: ExportedData = JSON.parse(decryptedJson);

    // Vérifier la structure des données
    if (!importedData.payouts || !importedData.settings || !importedData.miningConfigs) {
      throw new Error('Format de données invalide');
    }

    // Si on remplace les données existantes, on les efface d'abord
    if (options.replaceExisting && !options.mergeData) {
      clearAllData();
    }

    // Importer les données
    if (importedData.payouts) {
      const existingPayouts = options.mergeData ? await loadPayoutsFromStorage() : [];
      const mergedPayouts = options.mergeData ? 
        [...existingPayouts, ...importedData.payouts] : 
        importedData.payouts;
      await savePayoutsToStorage(mergedPayouts);
    }

    if (importedData.settings) {
      const settingsToSave = options.mergeData ? 
        { ...(await loadSettingsFromStorage()), ...importedData.settings } : 
        importedData.settings;
      await saveSettingsToStorage(settingsToSave);
    }

    if (importedData.miningConfigs) {
      const existingConfigs = options.mergeData ? await loadMiningConfigs() : {};
      const configsToSave = options.mergeData ?
        { ...existingConfigs, ...importedData.miningConfigs } :
        importedData.miningConfigs;
      localStorage.setItem('cryptotrack_mining_configs', JSON.stringify(configsToSave));
    }

    console.log('Données importées avec succès');
  } catch (error) {
    console.error('Erreur lors de l\'import des données:', error);
    Sentry.captureException(error, {
      tags: { operation: 'importEncryptedData' },
      extra: { replaceExisting: options.replaceExisting, mergeData: options.mergeData }
    });
    if (error instanceof Error && error.message.includes('déchiffrement')) {
      throw new Error('Mot de passe incorrect ou données corrompues');
    }
    throw new Error('Impossible d\'importer les données');
  }
};

// Télécharger les données exportées en tant que fichier
export const downloadEncryptedData = async (password: string, filename?: string): Promise<void> => {
  try {
    const encryptedData = await exportEncryptedData(password);
    
    // Créer un blob avec les données chiffrées
    const blob = new Blob([encryptedData], { type: 'text/plain' });
    
    // Créer un lien de téléchargement
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `cryptotrack-backup-${new Date().toISOString().split('T')[0]}.enc`;
    
    // Déclencher le téléchargement
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Nettoyer l'URL
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Erreur lors du téléchargement:', error);
    Sentry.captureException(error, {
      tags: { operation: 'downloadEncryptedData' }
    });
    throw new Error('Impossible de télécharger les données');
  }
};

// Importer depuis fichier avec synchronisation serveur pour les utilisateurs connectés
export const importDataFromFile = async (
  file: File,
  password: string,
  options?: { replaceExisting?: boolean; mergeData?: boolean }
): Promise<{ imported: number; skipped: number } | void> => {
  try {
    // Lire le contenu du fichier
    const fileContent = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'));
      reader.readAsText(file);
    });

    // Déchiffrer les données
    const decryptedJson = await decryptData(fileContent, password);
    const importedData: ExportedData = JSON.parse(decryptedJson);

    // Vérifier la structure des données
    if (!importedData.payouts || !importedData.settings || !importedData.miningConfigs) {
      throw new Error('Format de données invalide');
    }

    // Vérifier si l'utilisateur est authentifié
    const authState = getAuthState();

    if (authState.isAuthenticated) {
      // Utilisateur connecté : synchroniser avec le serveur
      try {
        const result = await payoutApiService.bulkImport(importedData.payouts, {
          settings: importedData.settings,
          miningConfigs: importedData.miningConfigs,
        });

        // Invalider le cache React Query pour recharger les données
        // Cette logique sera gérée par le composant qui appelle cette fonction

        console.log('Données importées et synchronisées avec succès');
        return result;
      } catch (apiError) {
        console.error('Erreur lors de la synchronisation avec le serveur:', apiError);
        Sentry.captureException(apiError, {
          tags: { operation: 'importDataFromFile_apiSync' }
        });
        throw new Error('Impossible de synchroniser les données avec le serveur');
      }
    } else {
      // Utilisateur invité : importer uniquement dans le localStorage
      await importEncryptedData(fileContent, password, options);
    }
  } catch (error) {
    console.error('Erreur lors de l\'import depuis le fichier:', error);
    Sentry.captureException(error, {
      tags: { operation: 'importDataFromFile' },
      extra: { fileName: file.name, fileSize: file.size }
    });
    throw error;
  }
};